import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

import { useDispatch, useSelector } from 'react-redux';
import TagManager from 'react-gtm-module';
import { selectProfile } from 'store/profile/selectors';
import { selectIsAuthenticated } from 'store/app/selectors';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';

import { useLocation } from 'react-router-dom';

// locales
import { useTranslation } from 'react-i18next';
import { translations } from 'locales/i18n';

// store
import {
  sliceKey as newsSliceKey,
  reducer as newsReducer,
} from '../../store/news/slice';

import { actions as appActions } from '../../store/app/slice';

import { newsSaga } from '../../store/news/saga';

// components
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { LoadingSpinner } from '../../atoms/Loading';

// types
import { PagesTypes } from '../../types/pages';

// hooks
import { useWindowSize } from '../../hooks/useWindowSize';

// styles
import { Wrapper, Container, LoadMoreRow } from './styles';

// InView Library
import useInView from 'react-cool-inview';

// tabs
import 'react-tabs/style/react-tabs.css';
import axios from 'axios';
import { ISingleItemTypes } from '../../types/singleItem';
import CardsListWebinar from '../../components/features/CardsListWebinar';
import Hashtags from '../../components/features/Hashtags';
import { debounce } from 'lodash';
import { canonicalUrl } from 'config/canonicalUrl';
const baseUrlFuture = process.env.REACT_APP_API_BASE_URL + 'api/v1/webinars';
const baseUrlAll = process.env.REACT_APP_API_BASE_URL + 'api/v1/webinars/all';
const baseUrlLeft = process.env.REACT_APP_API_BASE_URL + 'api/v1/webinars/left';

export interface WebinarsPayload {
  page: number;
  per_page: number;
  category?: string;
  tags?: number[];
  load_more?: boolean;
}

export function BroadcastingLibraryPage() {
  // inject reducer && saga
  useInjectSaga({ key: newsSliceKey, saga: newsSaga });
  useInjectReducer({ key: newsSliceKey, reducer: newsReducer });

  // locales hook
  const { t } = useTranslation();

  // location hook
  const location = useLocation();
  const initialTag = (location.state as { initialTag: number }) || null;
  const initialTagsState: number[] = initialTag ? [initialTag.initialTag] : [];
  window.history.replaceState({}, document.title);

  const [showButtonMore, setShowButtonMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchedWebinars, setFetchedWebinars] = useState<
    ISingleItemTypes[] | []
  >([]);
  const [loading, setLoading] = React.useState(false);
  const [preloader, setPreloader] = React.useState(false);
  const [selectedTags, setSelectedTags] = React.useState<number[]>(
    initialTagsState,
  );

  const [category, setCategory] = React.useState<string>('all');

  // dispatch
  const dispatch = useDispatch();

  // selector
  const profileInfo = useSelector(selectProfile);
  const isLoggedIn = useSelector(selectIsAuthenticated);

  const { observe, unobserve, inView, scrollDirection, entry } = useInView({
    threshold: 1,
    onEnter: ({ scrollDirection, entry, observe, unobserve }) => {
      unobserve(); // To stop observing the current target element
      fetchMore();
    },
    onLeave: ({ scrollDirection, entry, observe, unobserve }) => {
      observe(); // To re-start observing the current target element
    },
  });

  // fetch webinars
  const fetchWebinars = payload => {
    setLoading(true);

    let url = baseUrlAll;

    if (payload.category) {
      if ('future' === payload.category) {
        url = baseUrlFuture;
      } else if ('left' === payload.category) {
        url = baseUrlLeft;
      }
    }

    axios
      .get(`${url}`, {
        params: payload,
        headers: {
          Authorization: 'Bearer ' + isLoggedIn,
        },
      })
      .then(response => {
        const webinars = response.data.data;
        const lastPage = response?.data?.meta?.last_page;
        const currentPage = response?.data?.meta?.current_page;

        if (currentPage === lastPage) {
          setShowButtonMore(false);
        } else {
          setCurrentPage(currentPage);
          setShowButtonMore(true);
        }

        if (payload.load_more) {
          setFetchedWebinars([...fetchedWebinars, ...webinars]);
        } else {
          setFetchedWebinars(webinars);
        }

        setPreloader(false);
        setLoading(false);
      })
      .catch(error => console.error(`Error: ${error}`));
  };

  const fetchWebinarsWithTagsAndCategory = React.useCallback(
    debounce((tags, category) => {
      setPreloader(true);
      fetchWebinars({
        page: currentPage,
        per_page: 16,
        load_more: false,
        tags: tags,
        category: category,
      });
    }, 700),
    [],
  );

  // set initial state and handle tags change
  React.useEffect(() => {
    fetchWebinarsWithTagsAndCategory(selectedTags, category);
  }, [selectedTags, category]);

  useEffect(() => {
    dispatch(appActions.setLocation(location.pathname));
  }, []);

  // fetch more webinars
  const fetchMore = () => {
    let payload: WebinarsPayload;

    if (!loading) {
      payload = {
        page: currentPage + 1,
        per_page: 12,
        load_more: true,
        tags: selectedTags,
        category: category,
      };

      observe();
      fetchWebinars(payload);
    }
  };

  useEffect(() => {
    if (isLoggedIn && profileInfo?.data?.id) {
      const tagManagerArgs = {
        gtmId: process.env.REACT_APP_GTM_ID,
        dataLayer: {
          uid: profileInfo?.data?.id, //User ID. Id зарегистрированного пользователя.
        },
      };

      TagManager.initialize(tagManagerArgs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileInfo?.data?.id]);

  const selectTag = (id: number) => {
    setSelectedTags(prevState => {
      if (!prevState.includes(id)) {
        let result = prevState.concat(id);
        result.sort(function (a, b) {
          return a - b;
        });
        return result;
      } else return prevState;
    });
  };

  return (
    <>
      <Helmet>
        <title>{t(translations.broadcastingLibraryPage.seo.title)}</title>
        <meta
          name="description"
          content={t(translations.broadcastingLibraryPage.seo.description)}
        />
        <link rel="canonical" href={canonicalUrl + '/webinars'} />
      </Helmet>
      <Wrapper>
        <Breadcrumbs />
        <Hashtags
          tagsUrl={'api/v1/webinars/main/tags'}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
          pageTitle={t(translations.broadcastingLibraryPage.title)}
          categories={[
            {
              id: 0,
              type: 'all',
              title: t(translations.broadcastingLibraryPage.filter.all),
            },
            {
              id: 1,
              type: 'future',
              title: t(translations.broadcastingLibraryPage.filter.future),
            },
            {
              id: 2,
              type: 'left',
              title: t(translations.broadcastingLibraryPage.filter.past),
            },
          ]}
          setCategory={setCategory}
          activeCategory={category}
        />

        {preloader ? (
          <LoadMoreRow>
            <div>
              <LoadingSpinner />
            </div>
          </LoadMoreRow>
        ) : (
          <>
            <Container>
              <CardsListWebinar
                type={PagesTypes.Webinar}
                data={fetchedWebinars}
                clickAction={selectTag}
              />
            </Container>

            <LoadMoreRow>
              {showButtonMore && (
                <div ref={observe}>
                  <LoadingSpinner />
                </div>
              )}
            </LoadMoreRow>
          </>
        )}
      </Wrapper>
    </>
  );
}
